import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { EntityServices } from "@sdk/utils/entity.service";
import { iQCRuleGroup } from "./qc-rule-groups.models";
import {
  iQCAgentSummary,
  iQCResult,
  iQCResultsSummary,
} from "./qc-results.models";

export class AgentQualityControlServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
    this.qcRuleGroups.configure(config);
    this.qcResults.configure(config);
  }

  constructor(protected config: { basePath: string; token: string }) {
    this.qcRuleGroups.configure(config);
    this.qcResults.configure(config);
  }

  qcRuleGroups = Object.assign(
    new EntityServices<iQCRuleGroup>(SDKConfig, "qc-rule-groups"),
    {
      bootstrapDefaults: async (): Promise<{
        created: number;
        ruleGroups: iQCRuleGroup[];
      }> => {
        const results: AxiosResponse<{
          created: number;
          ruleGroups: iQCRuleGroup[];
        }> = await axios.post(
          this.config.basePath + "/qc-rule-groups/bootstrap-defaults",
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          },
        );
        return results.data;
      },
    },
  ) as EntityServices<iQCRuleGroup> & {
    bootstrapDefaults: () => Promise<{
      created: number;
      ruleGroups: iQCRuleGroup[];
    }>;
  };

  qcResults = Object.assign(
    new EntityServices<iQCResult>(SDKConfig, "qc-results"),
    {
      getSummary: async (query?: Record<string, unknown>) => {
        const results: AxiosResponse<iQCResultsSummary> = await axios.post(
          this.config.basePath + "/qc-results/summary",
          { query },
          {
            headers: {
              Authorization: this.config.token,
            },
          },
        );
        return results.data;
      },
      getAgentSummary: async (query?: Record<string, unknown>) => {
        const results: AxiosResponse<iQCAgentSummary[]> = await axios.post(
          this.config.basePath + "/qc-results/agent-summary",
          { query },
          {
            headers: {
              Authorization: this.config.token,
            },
          },
        );
        return results.data;
      },
    },
  ) as EntityServices<iQCResult> & {
    getSummary: (
      query?: Record<string, unknown>,
    ) => Promise<iQCResultsSummary>;
    getAgentSummary: (
      query?: Record<string, unknown>,
    ) => Promise<iQCAgentSummary[]>;
  };

  agentQualityControl = {
    getTranscriptMd: async (
      conversationId: string,
    ): Promise<{ transcriptMd: string }> => {
      const results: AxiosResponse<{ transcriptMd: string }> = await axios.get(
        this.config.basePath +
          `/agent-quality-control/conversations/${conversationId}/transcript-md`,
        {
          headers: {
            Authorization: this.config.token,
          },
        },
      );
      return results.data;
    },

    runQC: async (conversationId: string): Promise<iQCResult> => {
      const results: AxiosResponse<iQCResult> = await axios.post(
        this.config.basePath +
          `/agent-quality-control/conversations/${conversationId}/run-qc`,
        {},
        {
          headers: {
            Authorization: this.config.token,
          },
        },
      );
      return results.data;
    },

    getLatestQCResult: async (
      conversationId: string,
    ): Promise<{ qcResult: iQCResult | null }> => {
      const results: AxiosResponse<{ qcResult: iQCResult | null }> =
        await axios.get(
          this.config.basePath +
            `/agent-quality-control/conversations/${conversationId}/latest-qc-result`,
          {
            headers: {
              Authorization: this.config.token,
            },
          },
        );
      return results.data;
    },
  };
}
